import { supabase } from './supabase';
import { USE_MOCK_DATA } from './mockData';

export interface ClientPortfolio {
  id: string;
  company_name: string;
  email: string;
  phone?: string;
  status?: 'active' | 'inactive' | 'onboarding' | 'churned';
  tier?: 'basic' | 'standard' | 'premium' | 'enterprise';
  logo_url?: string;
  website?: string;
  account_manager_id?: string;
  created_at: string;
  metadata?: {
    industry?: string;
    company_size?: string;
    target_audience?: string;
    brand_voice?: string;
    business_objectives?: string[];
    custom_fields?: Record<string, any>;
  };
  performance?: {
    total_posts?: number;
    total_engagements?: number;
    engagement_rate?: number;
    platforms_count?: number;
    plugins_count?: number;
  };
}

export interface ClientNote {
  id?: string;
  client_id: string;
  title?: string;
  content: string;
  note_type?: 'general' | 'meeting' | 'call' | 'email' | 'issue' | 'success';
  is_important?: boolean;
  tags?: string[];
  author_id?: string;
  created_at?: string;
}

export interface ClientGoal {
  id?: string;
  client_id: string;
  goal_name: string;
  goal_type: 'engagement' | 'followers' | 'reach' | 'conversions' | 'revenue' | 'custom';
  target_value: number;
  current_value?: number;
  unit?: string;
  deadline?: string;
  status?: 'active' | 'completed' | 'paused' | 'cancelled';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  description?: string;
}

export interface ClientDocument {
  id?: string;
  client_id: string;
  document_name: string;
  document_type?: string;
  file_url: string;
  file_size?: number;
  mime_type?: string;
  category?: 'contract' | 'brief' | 'report' | 'asset' | 'other';
  tags?: string[];
  uploaded_by?: string;
}

export interface ClientTeamMember {
  id?: string;
  client_id: string;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  role?: string;
  is_primary_contact?: boolean;
  notes?: string;
}

export interface ClientActivity {
  id?: string;
  client_id: string;
  activity_type: string;
  activity_title: string;
  description?: string;
  actor_id?: string;
  actor_name?: string;
  metadata?: Record<string, any>;
  created_at?: string;
}

class PortfolioService {
  /**
   * Get all client portfolios with summary data
   */
  async getPortfolios(filters?: {
    status?: string;
    tier?: string;
    search?: string;
  }) {
    if (USE_MOCK_DATA) {
      const { mockClients, mockClientMetadata } = await import('./mockData');
      return {
        success: true,
        data: mockClients.map(c => {
          const metadata = mockClientMetadata.find(m => m.client_id === c.id);
          return {
            ...c,
            status: 'active',
            tier: 'standard',
            metadata,
            performance: {
              total_posts: Math.floor(Math.random() * 100),
              total_engagements: Math.floor(Math.random() * 10000),
              engagement_rate: (Math.random() * 5 + 1).toFixed(2),
              platforms_count: Math.floor(Math.random() * 5) + 1,
              plugins_count: Math.floor(Math.random() * 4) + 1
            }
          };
        })
      };
    }

    try {
      let query = supabase
        .from('clients')
        .select(`
          *,
          client_metadata (
            industry,
            company_size,
            target_audience,
            brand_voice,
            business_objectives,
            custom_fields
          )
        `);

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.tier) {
        query = query.eq('tier', filters.tier);
      }

      if (filters?.search) {
        query = query.or(`company_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      // Enrich with performance data
      const enrichedData = await Promise.all(
        (data || []).map(async (client) => {
          const performance = await this.getClientPerformance(client.id);
          return {
            ...client,
            metadata: client.client_metadata?.[0],
            performance: performance.data
          };
        })
      );

      return { success: true, data: enrichedData };
    } catch (error: any) {
      console.error('[PortfolioService] Error getting portfolios:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get a single client portfolio with full details
   */
  async getPortfolio(clientId: string) {
    if (USE_MOCK_DATA) {
      const { mockClients, mockClientMetadata } = await import('./mockData');
      const client = mockClients.find(c => c.id === clientId);
      const metadata = mockClientMetadata.find(m => m.client_id === clientId);

      return {
        success: true,
        data: {
          ...client,
          status: 'active',
          tier: 'standard',
          metadata,
          performance: {
            total_posts: 156,
            total_engagements: 24500,
            engagement_rate: 3.2,
            platforms_count: 4,
            plugins_count: 3
          }
        }
      };
    }

    try {
      const { data, error } = await supabase
        .from('clients')
        .select(`
          *,
          client_metadata (
            industry,
            company_size,
            website,
            target_audience,
            brand_voice,
            business_objectives,
            marketing_channels,
            custom_fields
          )
        `)
        .eq('id', clientId)
        .single();

      if (error) throw error;

      const performance = await this.getClientPerformance(clientId);

      return {
        success: true,
        data: {
          ...data,
          metadata: data.client_metadata?.[0],
          performance: performance.data
        }
      };
    } catch (error: any) {
      console.error('[PortfolioService] Error getting portfolio:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a new client portfolio
   */
  async createPortfolio(portfolio: Partial<ClientPortfolio>) {
    if (USE_MOCK_DATA) {
      console.log('[PortfolioService] Mock mode: Would create portfolio', portfolio);
      return {
        success: true,
        data: {
          id: `mock_${Date.now()}`,
          ...portfolio,
          created_at: new Date().toISOString()
        }
      };
    }

    try {
      // Create client record
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .insert({
          company_name: portfolio.company_name,
          email: portfolio.email,
          phone: portfolio.phone,
          website: portfolio.website,
          status: portfolio.status || 'onboarding',
          tier: portfolio.tier || 'standard',
          logo_url: portfolio.logo_url
        })
        .select()
        .single();

      if (clientError) throw clientError;

      // Create metadata if provided
      if (portfolio.metadata) {
        await supabase.from('client_metadata').insert({
          client_id: client.id,
          ...portfolio.metadata
        });
      }

      // Log activity
      await this.logActivity(client.id, {
        activity_type: 'client_created',
        activity_title: 'Client Created',
        description: `New client ${portfolio.company_name} was onboarded`
      });

      return { success: true, data: client };
    } catch (error: any) {
      console.error('[PortfolioService] Error creating portfolio:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update a client portfolio
   */
  async updatePortfolio(clientId: string, updates: Partial<ClientPortfolio>) {
    if (USE_MOCK_DATA) {
      console.log('[PortfolioService] Mock mode: Would update portfolio', clientId, updates);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('clients')
        .update({
          company_name: updates.company_name,
          email: updates.email,
          phone: updates.phone,
          website: updates.website,
          status: updates.status,
          tier: updates.tier,
          logo_url: updates.logo_url,
          updated_at: new Date().toISOString()
        })
        .eq('id', clientId);

      if (error) throw error;

      // Update metadata if provided
      if (updates.metadata) {
        await supabase
          .from('client_metadata')
          .upsert({
            client_id: clientId,
            ...updates.metadata
          }, {
            onConflict: 'client_id'
          });
      }

      return { success: true };
    } catch (error: any) {
      console.error('[PortfolioService] Error updating portfolio:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get client performance metrics
   */
  async getClientPerformance(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          total_posts: 156,
          total_engagements: 24500,
          engagement_rate: 3.2,
          platforms_count: 4,
          plugins_count: 3,
          total_reach: 125000,
          follower_growth: 1250
        }
      };
    }

    try {
      // Get latest metrics
      const { data: metrics, error: metricsError } = await supabase
        .from('client_performance_metrics')
        .select('*')
        .eq('client_id', clientId)
        .order('metric_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (metricsError) throw metricsError;

      // Get platform and plugin counts
      const [platformCount, pluginCount] = await Promise.all([
        supabase
          .from('client_platforms')
          .select('*', { count: 'exact', head: true })
          .eq('client_id', clientId)
          .eq('is_active', true),
        supabase
          .from('client_plugins')
          .select('*', { count: 'exact', head: true })
          .eq('client_id', clientId)
          .eq('is_connected', true)
      ]);

      return {
        success: true,
        data: {
          ...metrics,
          platforms_count: platformCount.count || 0,
          plugins_count: pluginCount.count || 0
        }
      };
    } catch (error: any) {
      console.error('[PortfolioService] Error getting performance:', error);
      return { success: false, error: error.message, data: {} };
    }
  }

  /**
   * Get client notes
   */
  async getNotes(clientId: string, limit: number = 50) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'note_1',
            client_id: clientId,
            title: 'Kickoff Meeting',
            content: 'Discussed Q1 strategy and content calendar. Client wants to focus on Instagram and LinkedIn.',
            note_type: 'meeting',
            is_important: true,
            created_at: new Date(Date.now() - 86400000).toISOString()
          },
          {
            id: 'note_2',
            client_id: clientId,
            title: 'Follow-up Call',
            content: 'Client approved the content plan. Ready to start posting next week.',
            note_type: 'call',
            is_important: false,
            created_at: new Date(Date.now() - 172800000).toISOString()
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('client_notes')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[PortfolioService] Error getting notes:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Create a client note
   */
  async createNote(note: ClientNote) {
    if (USE_MOCK_DATA) {
      console.log('[PortfolioService] Mock mode: Would create note', note);
      return {
        success: true,
        data: { id: `note_${Date.now()}`, ...note, created_at: new Date().toISOString() }
      };
    }

    try {
      const { data, error } = await supabase
        .from('client_notes')
        .insert(note)
        .select()
        .single();

      if (error) throw error;

      await this.logActivity(note.client_id, {
        activity_type: 'note_created',
        activity_title: 'Note Added',
        description: note.title || 'New note added'
      });

      return { success: true, data };
    } catch (error: any) {
      console.error('[PortfolioService] Error creating note:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get client goals
   */
  async getGoals(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'goal_1',
            client_id: clientId,
            goal_name: 'Reach 10K Instagram followers',
            goal_type: 'followers',
            target_value: 10000,
            current_value: 7500,
            unit: 'followers',
            deadline: new Date(Date.now() + 90 * 86400000).toISOString(),
            status: 'active',
            priority: 'high'
          },
          {
            id: 'goal_2',
            client_id: clientId,
            goal_name: 'Achieve 5% engagement rate',
            goal_type: 'engagement',
            target_value: 5,
            current_value: 3.2,
            unit: '%',
            status: 'active',
            priority: 'medium'
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('client_goals')
        .select('*')
        .eq('client_id', clientId)
        .order('priority', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[PortfolioService] Error getting goals:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Create a client goal
   */
  async createGoal(goal: ClientGoal) {
    if (USE_MOCK_DATA) {
      console.log('[PortfolioService] Mock mode: Would create goal', goal);
      return {
        success: true,
        data: { id: `goal_${Date.now()}`, ...goal, current_value: 0, status: 'active' }
      };
    }

    try {
      const { data, error } = await supabase
        .from('client_goals')
        .insert(goal)
        .select()
        .single();

      if (error) throw error;

      await this.logActivity(goal.client_id, {
        activity_type: 'goal_created',
        activity_title: 'Goal Created',
        description: `New goal: ${goal.goal_name}`
      });

      return { success: true, data };
    } catch (error: any) {
      console.error('[PortfolioService] Error creating goal:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update goal progress
   */
  async updateGoalProgress(goalId: string, currentValue: number) {
    if (USE_MOCK_DATA) {
      console.log('[PortfolioService] Mock mode: Would update goal progress', goalId, currentValue);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('client_goals')
        .update({ current_value: currentValue, updated_at: new Date().toISOString() })
        .eq('id', goalId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('[PortfolioService] Error updating goal progress:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get client activities
   */
  async getActivities(clientId: string, limit: number = 50) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'act_1',
            client_id: clientId,
            activity_type: 'post_published',
            activity_title: 'Post Published',
            description: 'Instagram post published successfully',
            created_at: new Date(Date.now() - 3600000).toISOString()
          },
          {
            id: 'act_2',
            client_id: clientId,
            activity_type: 'goal_updated',
            activity_title: 'Goal Progress Updated',
            description: 'Follower count updated to 7,500',
            created_at: new Date(Date.now() - 7200000).toISOString()
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('client_activities')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[PortfolioService] Error getting activities:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Log an activity
   */
  async logActivity(clientId: string, activity: Partial<ClientActivity>) {
    if (USE_MOCK_DATA) {
      console.log('[PortfolioService] Mock mode: Would log activity', activity);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('client_activities')
        .insert({
          client_id: clientId,
          activity_type: activity.activity_type,
          activity_title: activity.activity_title,
          description: activity.description,
          actor_id: activity.actor_id,
          actor_name: activity.actor_name,
          metadata: activity.metadata
        });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('[PortfolioService] Error logging activity:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get client team members
   */
  async getTeamMembers(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'team_1',
            client_id: clientId,
            name: 'Sarah Johnson',
            title: 'Marketing Director',
            email: 'sarah@client.com',
            phone: '+1-555-0100',
            is_primary_contact: true
          },
          {
            id: 'team_2',
            client_id: clientId,
            name: 'Mike Chen',
            title: 'Social Media Manager',
            email: 'mike@client.com',
            is_primary_contact: false
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('client_team_members')
        .select('*')
        .eq('client_id', clientId)
        .order('is_primary_contact', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[PortfolioService] Error getting team members:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Add team member
   */
  async addTeamMember(member: ClientTeamMember) {
    if (USE_MOCK_DATA) {
      console.log('[PortfolioService] Mock mode: Would add team member', member);
      return {
        success: true,
        data: { id: `team_${Date.now()}`, ...member }
      };
    }

    try {
      const { data, error } = await supabase
        .from('client_team_members')
        .insert(member)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error: any) {
      console.error('[PortfolioService] Error adding team member:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get portfolio analytics
   */
  async getAnalytics(clientId: string, days: number = 30) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          summary: {
            total_posts: 45,
            total_engagements: 8750,
            avg_engagement_rate: 3.5,
            follower_growth: 850,
            reach_growth: 12.5
          },
          trends: Array.from({ length: days }, (_, i) => ({
            date: new Date(Date.now() - (days - i) * 86400000).toISOString().split('T')[0],
            posts: Math.floor(Math.random() * 3) + 1,
            engagements: Math.floor(Math.random() * 500) + 100,
            reach: Math.floor(Math.random() * 2000) + 500
          }))
        }
      };
    }

    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('client_performance_metrics')
        .select('*')
        .eq('client_id', clientId)
        .gte('metric_date', startDate.toISOString().split('T')[0])
        .order('metric_date', { ascending: true });

      if (error) throw error;

      // Calculate summary
      const summary = (data || []).reduce((acc, day) => ({
        total_posts: acc.total_posts + (day.total_posts || 0),
        total_engagements: acc.total_engagements + (day.total_engagements || 0),
        avg_engagement_rate: 0, // Calculate below
        follower_growth: acc.follower_growth + (day.follower_growth || 0),
        reach_growth: 0 // Calculate below
      }), {
        total_posts: 0,
        total_engagements: 0,
        avg_engagement_rate: 0,
        follower_growth: 0,
        reach_growth: 0
      });

      // Calculate averages
      if (data && data.length > 0) {
        const validEngagementRates = data.filter(d => d.engagement_rate).map(d => d.engagement_rate);
        summary.avg_engagement_rate = validEngagementRates.reduce((a, b) => a + b, 0) / validEngagementRates.length;

        if (data.length > 1 && data[0].total_reach && data[data.length - 1].total_reach) {
          summary.reach_growth = ((data[data.length - 1].total_reach - data[0].total_reach) / data[0].total_reach) * 100;
        }
      }

      return {
        success: true,
        data: {
          summary,
          trends: data || []
        }
      };
    } catch (error: any) {
      console.error('[PortfolioService] Error getting analytics:', error);
      return { success: false, error: error.message, data: { summary: {}, trends: [] } };
    }
  }
}

export const portfolioService = new PortfolioService();
